import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AutoSizer, List, WindowScroller } from 'react-virtualized';

import useIsFirstRender from '@/hooks/useIsFirstRender';
import useScreenSize from '@/hooks/useScreenSize';
import HomeUserCard from '@/shared-components/home/components/HomeUserCard';
import SearchMoreCard from '@/shared-components/home/components/SearchMoreCard';
import InfiniteScroll from '@/shared-components/lists/components/InfiniteScroll';

import './styles/VirtualList.css';

function CardSizer({ width, sampleItem, onMeasure }) {
  const sizerWrapRef = useRef(null);

  const measure = useCallback(() => {
    if (!sizerWrapRef.current) return;
    const measuredHeight = sizerWrapRef.current.offsetHeight;

    if (measuredHeight && typeof onMeasure === 'function') {
      onMeasure(measuredHeight);
    }
  }, [onMeasure]);

  useEffect(() => {
    const timeoutId = setTimeout(measure, 0);
    return () => clearTimeout(timeoutId);
  }, [measure, width, sampleItem]);

  useEffect(() => {
    if (typeof document === 'undefined' || !document.fonts?.ready) {
      return;
    }

    document.fonts.ready.then(() => measure()).catch(() => {});
  }, [measure]);

  if (!sampleItem) {
    return null;
  }

  return (
    <div style={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none', zIndex: -1 }}>
      <div style={{ width, boxSizing: 'border-box' }} ref={sizerWrapRef}>
        <HomeUserCard userData={sampleItem} style={{ width }} />
      </div>
    </div>
  );
}

export default function VirtualList({ items, onLoadMore, showSearchCTA = false, fromSearch = false }) {
  const navigate = useNavigate();
  const listRef = useRef();
  const screenSize = useScreenSize();
  const isFirstRender = useIsFirstRender();
  const [measuredCardHeight, setMeasuredCardHeight] = useState(null);

  const GAP = fromSearch
    ? (screenSize.width <= 600 ? 10 : 15)
    : screenSize.width <= 460 ? 12
      : screenSize.width <= 580 ? 15
        : screenSize.width <= 1000 ? 15
          : 20;

  // Size cards from the width AutoSizer gives the list, so the grid fills its container exactly.
  function computeListSizesFromWidth(containerWidth) {
    const safeWidth = Math.max(containerWidth, 0);

    const columnsPerRow =
      containerWidth <= 692 ? 2 :
        containerWidth <= 950 ? 3 :
          containerWidth <= 1350 ? 4 : 5;

    const totalGaps   = (columnsPerRow - 1) * GAP;
    const columnWidth = Math.max(0, (safeWidth - totalGaps) / columnsPerRow);

    // fallback aproximado basado en relación imagen + contenido
    const fallback = Math.round(columnWidth * 0.53 + 170);
    const rowHeight = (measuredCardHeight ?? fallback) + GAP;

    return { columnWidth, columnsPerRow, gap: GAP, rowHeight };
  }

  // recompute cuando cambie el tamaño de pantalla o la longitud
  useEffect(() => {
    if (!isFirstRender) listRef.current?.recomputeRowHeights();
  }, [screenSize, measuredCardHeight, items?.data?.length, isFirstRender]);

  useEffect(() => {
    listRef.current?.recomputeRowHeights();
  }, [items?.data?.length]);

  return (
    <InfiniteScroll
      onLoadMore={onLoadMore}
      shouldLoadMore={items.data.length < items.totalDocs}
      offsetElements={3}
      listLength={items.data.length}
      className={`home-galleries__infinite-container${fromSearch ? ' home-galleries__infinite-container--from-search' : ''}`}
    >
      {({ infiniteScrollAssigner }) => (
        <WindowScroller>
          {({ height, isScrolling, onChildScroll, scrollTop }) => (
            <AutoSizer disableHeight>
              {({ width }) => {
                const s = computeListSizesFromWidth(width || 0.1);
                const sample = items.data[0];
                const previewLimit = s.columnsPerRow >= 5 ? 24 : 23;
                const displayItems = showSearchCTA
                  ? [...items.data.slice(0, previewLimit), { _searchCTA: true }]
                  : items.data;

                return (
                  <>
                    <CardSizer
                      width={`${s.columnWidth}px`}
                      sampleItem={sample}
                      onMeasure={(value) => {
                        if (value && value !== measuredCardHeight) {
                          setMeasuredCardHeight(value);
                        }
                      }}
                    />

                    <List
                      autoHeight
                      width={width === 0 ? 0.1 : width}
                      height={height}
                      isScrolling={isScrolling}
                      onScroll={onChildScroll}
                      scrollTop={scrollTop}
                      ref={listRef}
                      overscanRowCount={3}
                      rowCount={Math.ceil(displayItems.length / s.columnsPerRow)}
                      rowHeight={s.rowHeight}
                      estimatedRowSize={s.rowHeight}
                      rowRenderer={({ index, key, style }) => {
                        const cols = [];
                        const from = index * s.columnsPerRow;
                        const to   = Math.min(from + s.columnsPerRow, displayItems.length);

                        for (let i = from; i < to; i++) {
                          const currentItem = displayItems[i];
                          if (currentItem?._searchCTA) {
                            cols.push(
                              <SearchMoreCard
                                key={`search-cta-${i}`}
                                style={{ width: `${s.columnWidth}px` }}
                                message="Want to discover all galleries?"
                                actionLabel="Search Galleries"
                                onClick={() =>
                                  navigate("/search", { state: { category: "Galleries" } })
                                }
                              />
                            );
                          } else {
                            cols.push(
                              <HomeUserCard
                                key={currentItem._id ?? i}
                                userData={currentItem}
                                style={{ width: `${s.columnWidth}px` }}
                                infiniteScrollObserver={infiniteScrollAssigner(i)}
                              />
                            );
                          }
                        }

                        return (
                          <div
                            className="home-galleries__inner-virtual-row"
                            key={key}
                            style={{ ...style, marginBottom: s.gap }}
                          >
                            <div
                              style={{
                                width: '100%',
                                margin: 0,
                                display: 'grid',
                                gridTemplateColumns: `repeat(${s.columnsPerRow}, ${s.columnWidth}px)`,
                                columnGap: `${s.gap}px`,
                                alignItems: 'stretch',
                                justifyContent: 'start',
                              }}
                            >
                              {cols}
                            </div>
                          </div>
                        );
                      }}
                    />
                  </>
                );
              }}
            </AutoSizer>
          )}
        </WindowScroller>
      )}
    </InfiniteScroll>
  );
}
